// @mui
import { Box, BoxProps } from "@mui/material";
import { alpha, lighten, styled, Theme, useTheme } from "@mui/material/styles";
// theme
import { ColorSchema } from "../theme/palette";

// ----------------------------------------------------------------------

export type LabelColor =
  | "default"
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error";

type LabelVariant = "filled" | "outlined" | "ghost";

const RootStyle = styled("span")(
  ({
    theme,
    ownerState,
  }: {
    theme: Theme;
    ownerState: {
      color: LabelColor;
      variant: LabelVariant;
    };
  }) => {
    const isLight = theme.palette.mode === "light";
    const { color, variant } = ownerState;

    const styleFilled = (color: ColorSchema) => ({
      color: theme.palette[color].contrastText,
      backgroundColor: theme.palette[color].main,
    });

    const styleOutlined = (color: ColorSchema) => ({
      color: theme.palette[color].dark,
      backgroundColor: theme.palette[color]["light"],
      border: `1px solid ${theme.palette[color].dark}`,
    });

    const styleGhost = (color: ColorSchema) => ({
      color: theme.palette[color][isLight ? "dark" : "light"],
      backgroundColor: alpha(theme.palette[color].main, 0.16),
      border: `1px solid ${theme.palette[color].dark}`,
    });

    return {
      height: 22,
      minWidth: 22,
      lineHeight: 0,
      borderRadius: "12px",
      cursor: "default",
      alignItems: "center",
      whiteSpace: "nowrap",
      display: "inline-flex",
      justifyContent: "center",
      padding: theme.spacing(0, 1),
      color: theme.palette.grey[800],
      fontSize: theme.typography.pxToRem(10),
      fontFamily: theme.typography.fontFamily,
      backgroundColor: theme.palette.grey[300],
      fontWeight: theme.typography.fontWeightMedium,

      ...(color !== "default"
        ? {
            ...(variant === "filled" && { ...styleFilled(color) }),
            ...(variant === "outlined" && color === "primary"
              ? {
                  color: theme.palette.secondary.dark,
                  backgroundColor: theme.palette.text.input,
                  border: `1px solid ${theme.palette.secondary.main}`,
                }
              : { ...styleOutlined(color) }),
            ...(variant === "ghost" && { ...styleGhost(color) }),
          }
        : {
            ...(variant === "ghost" && {
              color: isLight
                ? theme.palette.text.greyDark
                : theme.palette.common.white,
              border: `1px solid ${theme.palette.text.greyDark}`,
              backgroundColor: theme.palette.grey[500_16],
            }),
            ...(variant === "outlined" && {
              color: isLight
                ? theme.palette.text.body
                : theme.palette.common.white,
              border: `1px solid ${theme.palette.text.body}`,
              backgroundColor: isLight
                ? lighten(theme.palette.grey[500_16], 0.48)
                : theme.palette.grey[800_16],
            }),
          }),
    };
  },
);

// ----------------------------------------------------------------------

interface Props extends BoxProps {
  startIcon?: React.ReactElement | null;
  endIcon?: React.ReactElement | null;
  color?: LabelColor;
  variant?: LabelVariant;
  onMouseEnter?: React.MouseEventHandler<HTMLSpanElement>;
  onMouseDown?: React.MouseEventHandler<HTMLSpanElement>;
  innerSx?: any;
}

export default function Label({
  children,
  color = "default",
  variant = "ghost",
  startIcon,
  endIcon,
  sx,
  innerSx,
  onMouseLeave,
  onMouseEnter,
}: Props) {
  const theme = useTheme();

  const style = {
    width: 16,
    height: 16,
    "& svg, img": { width: 1, height: 1, objectFit: "cover" },
  };

  return (
    <RootStyle
      ownerState={{ color, variant }}
      sx={{
        ...(startIcon && { pl: 0.75 }),
        ...(endIcon && { pr: 0.75 }),
        ...sx,
      }}
      theme={theme}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box sx={innerSx}>
        {startIcon && <Box sx={{ mr: 0.75, ...style }}>{startIcon}</Box>}

        {children}

        {endIcon && <Box sx={{ ml: 0.75, ...style }}>{endIcon}</Box>}
      </Box>
    </RootStyle>
  );
}
